import type { AutocompleteChangeReason, IconButtonProps, SxProps } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Icon from 'components/icon';
import Clear from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import type { PaperProps } from '@mui/material/Paper';
import type { PopperProps } from '@mui/material/Popper';
import type { Theme } from '@mui/material/styles';

export type SidePanelAutoCompleteProps<T> = {
  options: Array<T>;
  value: T | null | undefined;
  isLoading?: boolean;
  testId?: string;
  textDataTestId?: string;
  label?: string | any;
  noOptionsText?: string;
  isDisabled?: boolean;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  renderOptions: (props: React.HTMLAttributes<HTMLLIElement> & { key: any }, options: T) => JSX.Element;
  isOptionEqualToValue: (option: T, value: T) => boolean;
  onClear?: () => void;
  getOptionLabel?: (option: T) => string;
  onChange?: (newValue: T | null) => void;
  onInputChange?: (value: string, reason: string) => void;
  filterOptions?: (options: T[]) => T[];
  hideSearchIcon?: boolean;
  disableClearable?: boolean;
  slotProps?: {
    clearIndicator?: Partial<IconButtonProps>;
    paper?: PaperProps;
    popper?: Partial<PopperProps>;
    popupIndicator?: Partial<IconButtonProps>;
  };
  sx?: SxProps<Theme>;
};

const CLEAR_REASON: AutocompleteChangeReason = 'clear';

function SidePanelAutoComplete<T>(props: SidePanelAutoCompleteProps<T>) {
  const { t } = useTranslation();
  const defaultStyles: SxProps<Theme> = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: props.error ? 'red' : undefined,
      },
      '&:hover fieldset': {
        borderColor: props.error ? 'red' : undefined,
      },
      '&.Mui-focused fieldset': {
        borderColor: props.error ? 'red' : undefined,
        borderWidth: props.error ? '1px' : undefined,
      },
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: 'red',
    },
  };

  return (
    <Autocomplete
      id={props.testId}
      sx={(props.sx !== undefined ? [defaultStyles, props.sx] : defaultStyles) as SxProps<Theme>}
      disabled={props.isDisabled ?? false}
      slotProps={props.slotProps}
      autoHighlight
      data-testid={props.testId}
      onInputChange={(_event, value, reason) => (props.onInputChange ? props.onInputChange(value, reason) : null)}
      options={props.options}
      filterOptions={props.filterOptions}
      loading={props.isLoading ?? false}
      getOptionLabel={(option: any) => (props.getOptionLabel ? props.getOptionLabel(option) : option.label ?? '')}
      isOptionEqualToValue={props.isOptionEqualToValue}
      value={props.value}
      noOptionsText={props.noOptionsText ?? t('empty_state.filtering.title')}
      onChange={(_, newValue, reason) => (reason === CLEAR_REASON ? props.onClear?.() : props.onChange?.(newValue))}
      clearIcon={<Clear />}
      disableClearable={props.disableClearable ?? false}
      renderInput={(params) => (
        <TextField
          {...params}
          data-testid={props.textDataTestId}
          size="small"
          label={props.label}
          placeholder={props.placeholder}
          error={props.error}
          helperText={props.helperText}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-error fieldset': {
                borderColor: 'red',
              },
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: props.hideSearchIcon ? null : (
              <InputAdornment position="start">
                <Icon name="IcoSearch" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {props.isLoading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={props.renderOptions}
    />
  );
}

export default SidePanelAutoComplete;
